import { useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import ListGroup from 'react-bootstrap/ListGroup'
import { removeMember, type GroupDetail, type GroupMember } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useAuth } from '../../auth/AuthContext'
import { MemberFormModal } from './MemberFormModal'

export type MembersTabProps = {
    group: GroupDetail
    /** Offline se smí jen číst. */
    canWrite: boolean
    onChanged: () => void
}

export function MembersTab({ group, canWrite, onChanged }: MembersTabProps) {
    const { user } = useAuth()
    const [editing, setEditing] = useState<GroupMember | undefined>()
    const [showForm, setShowForm] = useState(false)

    const isAdmin = (user?.isAdmin ?? false) && canWrite

    const handleRemove = async (member: GroupMember) => {
        if (!window.confirm(`Opravdu odebrat ${member.displayName} ze skupiny?`)) {
            return
        }
        const result = await actionApiCall(() => removeMember({ path: { groupId: group.id, memberId: member.id } }), {
            successMessage: 'Člen byl odebrán.',
        })
        if (result !== undefined) {
            onChanged()
        }
    }

    return (
        <>
            {isAdmin && (
                <div className="d-flex justify-content-end mb-3">
                    <Button
                        className="su-tappable"
                        onClick={() => {
                            setEditing(undefined)
                            setShowForm(true)
                        }}
                    >
                        <i className="bi bi-person-plus me-1" aria-hidden="true" />
                        Přidat člena
                    </Button>
                </div>
            )}

            <Card>
                <ListGroup variant="flush">
                    {group.members.map((member) => (
                        <ListGroup.Item key={member.id} className="d-flex align-items-center gap-2">
                            <span
                                className="rounded-circle flex-shrink-0"
                                style={{ width: '0.9rem', height: '0.9rem', backgroundColor: member.color }}
                                aria-hidden="true"
                            />
                            <div className="flex-grow-1 min-w-0">
                                <div className="fw-semibold text-truncate">{member.displayName}</div>
                                <div className="small text-secondary text-truncate">
                                    {member.userLogin ? `účet ${member.userLogin}` : 'bez účtu'}
                                </div>
                            </div>
                            {!member.isActive && <Badge bg="secondary">Neaktivní</Badge>}
                            {isAdmin && (
                                <>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="su-tappable"
                                        aria-label={`Upravit člena ${member.displayName}`}
                                        onClick={() => {
                                            setEditing(member)
                                            setShowForm(true)
                                        }}
                                    >
                                        <i className="bi bi-pencil" aria-hidden="true" />
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="su-tappable"
                                        aria-label={`Odebrat člena ${member.displayName}`}
                                        onClick={() => void handleRemove(member)}
                                    >
                                        <i className="bi bi-trash" aria-hidden="true" />
                                    </Button>
                                </>
                            )}
                        </ListGroup.Item>
                    ))}
                    {group.members.length === 0 && (
                        <ListGroup.Item className="text-secondary">Skupina zatím nemá žádné členy.</ListGroup.Item>
                    )}
                </ListGroup>
            </Card>

            {showForm && (
                <MemberFormModal
                    show={showForm}
                    key={editing?.id ?? 'novy'}
                    groupId={group.id}
                    member={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={onChanged}
                />
            )}
        </>
    )
}
